import { fetchAuthSession } from 'aws-amplify/auth';
import { config } from '../config';

/**
 * Thin API client. Attaches the Cognito ID token (which carries custom:accountId / custom:role
 * stamped by the pre-token-gen trigger) as the Authorization header — the API Gateway JWT
 * authorizer validates it and handlers read the claims.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
  }
}

async function authHeader(): Promise<Record<string, string>> {
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();
  return idToken ? { Authorization: idToken } : {};
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { ...(await authHeader()) };
  if (body !== undefined) headers['content-type'] = 'application/json';

  const res = await fetch(`${config.apiEndpoint}${path}`, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  const text = await res.text();
  const parsed = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    throw new ApiError(res.status, parsed?.error ?? res.statusText, parsed);
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  // Body-carrying DELETE: account closure echoes the typed confirmation phrase, which the API verifies
  // server-side rather than trusting the client to have asked.
  del: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
};
